import {FormEvent, useEffect, useRef, useState} from 'react';
import {refreshAuthorizedWorkspace} from './workspaceNavigation';
import {TenantIdentity} from './TenantIdentity';
import {tenantBrandBySlug} from './tenantBranding';
import {authCompletionUrl,authJourneyUrl} from './authReturnDestination';
import {ServingBuildIdentity} from './ServingBuildIdentity';

type Props = {syntheticPreview?: boolean; onAuthenticated?: () => void; onCreateAccount?: () => void; tenantSlug?: string|null};

export function LoginApp({syntheticPreview = false, onAuthenticated, onCreateAccount, tenantSlug=null}: Props = {}) {
  const [status, setStatus] = useState<'idle'|'submitting'|'failed'|'uncertain'>('idle');
  const [checkingOutcome,setCheckingOutcome]=useState(false);
  const checkingRef=useRef(false);
  const [validation, setValidation] = useState('');
  const [signupAvailable,setSignupAvailable]=useState(false);
  const inFlight = useRef(false);
  const requestGeneration = useRef(0);
  const cancelRequest = useRef<(() => void)|null>(null);
  useEffect(() => () => { requestGeneration.current++; cancelRequest.current?.(); cancelRequest.current=null; }, []);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const failureRef = useRef<HTMLParagraphElement>(null);
  useEffect(()=>{let active=true;
    if(!syntheticPreview)void(async()=>{try{const response=await fetch('/api/v1/signup/config',{credentials:'omit',headers:{Accept:'application/json'}});if(response.ok){const value=await response.json();if(active)setSignupAvailable(value.enabled===true);}}catch{/* Sign-in remains usable when signup is unavailable. */}})();
    return()=>{active=false};
  },[syntheticPreview]);
  useEffect(()=>{if(status==='failed'||status==='uncertain')failureRef.current?.focus()},[status]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (inFlight.current) return;
    const data = new FormData(event.currentTarget);
    const email = String(data.get('email') ?? '').trim();
    const password = String(data.get('password') ?? '');
    setValidation('');
    setStatus('idle');
    if (!email.includes('@')) {
      setValidation('Enter your email address.');
      emailRef.current?.focus();
      return;
    }
    if (password === '') {
      setValidation('Enter your password.');
      passwordRef.current?.focus();
      return;
    }
    if (syntheticPreview) {
      if (!email.endsWith('.invalid')) {
        setValidation('Use the supplied synthetic .invalid email. Never enter real credentials.');
        emailRef.current?.focus();
        return;
      }
      onAuthenticated?.();
      return;
    }
    inFlight.current = true;
    setStatus('submitting');
    const owner=++requestGeneration.current, controller=new AbortController();
    let definitiveDenial=false;
    let timer:ReturnType<typeof setTimeout>|undefined;
    const deadline=new Promise<never>((_,reject)=>{
      timer=setTimeout(()=>{reject(new Error('Sign-in outcome unknown'));controller.abort();},25_000);
    });
    cancelRequest.current=()=>{clearTimeout(timer);controller.abort();};
    try {
      const completion = fetch('/api/v1/auth/login', {
        method: 'POST', credentials: 'same-origin', signal:controller.signal,
        headers: {'Accept': 'application/json', 'Content-Type': 'application/json'},
        body: JSON.stringify({email, password}),
      }).then(async response=>({response,body:await response.json() as unknown}));
      // Bound both receiving the response and decoding its body. An abort alone is not a deadline.
      const {response,body}=await Promise.race([completion,deadline]);
      if(owner!==requestGeneration.current)return;
      if(body===null||typeof body!=='object'||Array.isArray(body))throw new Error('Unknown response');
      const value=body as Record<string,unknown>;
      definitiveDenial=!response.ok&&(
        (response.status===401&&value.code==='identity.authentication_failed')||
        (response.status===429&&value.code==='identity.authentication_unavailable')
      );
      if (!response.ok || value.authenticated !== true) { throw new Error('Unconfirmed authentication'); }
      const destination=value.next === '/mfa' ? authJourneyUrl('/mfa') : authCompletionUrl();
      if(!refreshAuthorizedWorkspace(destination))window.location.assign(destination);
    } catch {
      if(owner!==requestGeneration.current)return;
      if(!definitiveDenial){
        // The server might have established a session: do not submit credentials again here.
        if(passwordRef.current)passwordRef.current.value='';
        setStatus('uncertain');
      }else{
        inFlight.current = false;
        setStatus('failed');
      }
    } finally {
      clearTimeout(timer);
      if(owner===requestGeneration.current)cancelRequest.current=null;
    }
  }

  async function checkSignIn(){
    if(status!=='uncertain'||checkingRef.current)return;
    checkingRef.current=true;setCheckingOutcome(true);
    const owner=++requestGeneration.current,controller=new AbortController();
    let timer:ReturnType<typeof setTimeout>|undefined;
    const deadline=new Promise<never>((_,reject)=>{timer=setTimeout(()=>{reject(new Error('Session outcome unknown'));controller.abort();},25_000);});
    cancelRequest.current=()=>{clearTimeout(timer);controller.abort();};
    try{
      const completion=fetch('/api/v1/auth/mfa',{method:'GET',credentials:'same-origin',headers:{Accept:'application/json'},signal:controller.signal})
        .then(async response=>({response,body:await response.json() as unknown}));
      const {response,body}=await Promise.race([completion,deadline]);
      if(owner!==requestGeneration.current)return;
      if(body===null||typeof body!=='object'||Array.isArray(body))throw new Error('Unknown response');
      const value=body as Record<string,unknown>;
      if(response.status===401&&value.code==='identity.session_required'){
        inFlight.current=false;setStatus('failed');return;
      }
      if(!response.ok||(value.next!=='/'&&value.next!=='/mfa'))throw new Error('Unconfirmed session');
      const destination=value.next==='/mfa'?authJourneyUrl('/mfa'):authCompletionUrl();
      if(!refreshAuthorizedWorkspace(destination))window.location.assign(destination);
    }catch{
      // Neither a denial nor transport failure proves absence. Keep credentials locked.
    }finally{
      clearTimeout(timer);
      if(owner===requestGeneration.current){cancelRequest.current=null;checkingRef.current=false;setCheckingOutcome(false);}
    }
  }

  const requestLocked=status==='submitting'||status==='uncertain';
  return <main className="shell access-shell">
    {syntheticPreview && <p className="preview-banner" role="status">Synthetic UI Preview — Not Production</p>}
    <header className="hero access-hero">
      <TenantIdentity context="login-card" brand={tenantBrandBySlug(tenantSlug)}/>
      <p className="eyebrow">SECURE ACCESS</p>
      <h1>Professional service. Verified access.</h1>
      <p>{syntheticPreview ? 'Use only the supplied invented credentials to preview the customer journey.' : 'Sign in to continue to your authorized organization workspace.'}</p>
    </header>
    <section className="panel access-panel" aria-labelledby="login-title">
      <h2 id="login-title">Sign in</h2>
      {syntheticPreview && <p className="notice">No account, authentication service, session, or credential storage is connected.</p>}
      {validation && <p className="notice error" role="alert">{validation}</p>}
      {status === 'failed' && <p ref={failureRef} className="notice error" role="alert" tabIndex={-1}>Authentication could not be completed. Check your details or try again later.</p>}
      {status==='uncertain'&&<p ref={failureRef} className="notice error" role="alert" tabIndex={-1}>The sign-in result could not be confirmed. <button type="button" className="button-link button-secondary" disabled={checkingOutcome} onClick={()=>void checkSignIn()}>{checkingOutcome?'Checking sign-in…':'Check sign-in'}</button> before trying again.</p>}
      <form className="login-form" onSubmit={submit} noValidate>
        <p className="form-note">Use the email and password you created for this account.</p>
        <label htmlFor="login-email">Email</label>
        <input className="login-email" ref={emailRef} disabled={requestLocked} id="login-email" name="email" type="email" autoComplete="username" autoCapitalize="none" spellCheck={false} defaultValue={syntheticPreview ? 'jordan.preview@example.invalid' : undefined} required />
        <label htmlFor="login-password">Password</label>
        <input className="login-password" ref={passwordRef} disabled={requestLocked} id="login-password" name="password" type="password" autoComplete="current-password" defaultValue={syntheticPreview ? 'preview-only' : undefined} required />
        <button className="button-primary" type="submit" disabled={requestLocked}>{status === 'submitting' ? 'Signing in…' : status==='uncertain' ? 'Sign-in not confirmed' : syntheticPreview ? 'Continue to synthetic verification' : 'Sign in'}</button>
      </form>
      {!syntheticPreview&&<p><a href="/forgot-password">Forgot your password?</a></p>}
      {syntheticPreview&&onCreateAccount&&<button className="button-link button-secondary" type="button" onClick={onCreateAccount}>Create a synthetic customer account</button>}
      {!syntheticPreview&&signupAvailable&&<p>New to ProFenz? <a href={authJourneyUrl('/sign-up')}>Create a customer account</a>.</p>}
    </section>
    <ServingBuildIdentity/>
  </main>;
}
